import { useCheckBulkPermissions } from "@/api/relations";
import { useWorkspaces } from "@/api/workspaces";
import { useMemo } from "react";

/**
 * The workspaces the actor may write to — the destinations a Move to / Copy
 * to is actually allowed to land in.
 *
 * The server is the authority here: it checks `can_edit` on the destination
 * for both verbs and refuses without it. This exists so the menu doesn't
 * invite a choice the commit will reject — the picker disables what this set
 * excludes rather than hiding it, so an unwritable workspace reads as "not
 * allowed" instead of quietly missing.
 *
 * Returns `undefined` while the check is in flight; callers treat that as
 * "assume writable" so the list is never briefly empty.
 */
export function useWritableWorkspaceIds(
  enabled = true,
): ReadonlySet<string> | undefined {
  const { data: workspaces = [] } = useWorkspaces();

  const checks = useMemo(
    () =>
      workspaces.map((ws) => ({
        resource_object_type: "workspace" as const,
        resource_object_id: ws.id,
        permission: "can_edit" as const,
      })),
    [workspaces],
  );

  const { data } = useCheckBulkPermissions(
    { checks },
    { enabled: enabled && checks.length > 0 },
  );

  return useMemo(() => {
    if (!data?.workspace) return undefined;
    const writable = new Set<string>();
    for (const ws of workspaces) {
      if (data.workspace[ws.id]?.can_edit) writable.add(ws.id);
    }
    return writable;
  }, [data, workspaces]);
}
